/** Shared geo-filter helpers for /csrp/dashboard and /csrp/approver-dashboard. */

import {
  isUserGeoAllScopeForDashboard,
  resolveGeoFilterSelection,
} from "./userTerritoryScope";

export const getMarketCode = (m) =>
  String(m?.marketCode || m?.MARKETCODE || m?.MarketCode || m?.id || "").trim();

export const getMarketName = (m) =>
  String(m?.marketName || m?.MARKETNAME || m?.MarketName || m?.text || "").trim();

/** Export market → Zone → Country → Region → Territory; Domestic → Country first. */
export const isExportMarketSelected = (marketCode, markets) => {
  const code = String(marketCode ?? "").trim();
  if (!code) return false;
  const list = Array.isArray(markets) ? markets : [];
  const m = list.find((x) => getMarketCode(x).toUpperCase() === code.toUpperCase());
  if (!m) return code.toUpperCase() === "M002";
  const name = getMarketName(m).toLowerCase();
  return name === "export" || name.includes("export") || code.toUpperCase() === "M002";
};

export const resolveCountryName = (countryCode, countries = []) => {
  const cc = String(countryCode ?? "").trim();
  if (!cc) return "";
  const row = countries.find(
    (c) => String(c.id || c.countryCode || c.CountryCode || "").trim() === cc
  );
  return String(row?.text || row?.countryName || row?.CountryName || "").trim();
};

export const formatZoneOptionLabel = (zone, countries = [], { includeCountry = true } = {}) => {
  const zoneName = String(zone.zoneName || zone.ZoneName || zone.text || "").trim();
  if (!includeCountry) {
    return zoneName || String(zone.id || zone.zoneCode || "").trim();
  }
  const countryLabel =
    String(zone.countryName || zone.CountryName || "").trim() ||
    resolveCountryName(zone.countryCode || zone.CountryCode, countries);
  if (!zoneName) return countryLabel || String(zone.id || zone.zoneCode || "").trim();
  return countryLabel ? `${zoneName} (${countryLabel})` : zoneName;
};

export const geoFilterArraysEqual = (a, b) => {
  const left = Array.isArray(a) ? [...a].map(String).sort() : [];
  const right = Array.isArray(b) ? [...b].map(String).sort() : [];
  return left.length === right.length && left.every((v, i) => v === right[i]);
};

export const joinGeoFilter = (val) => {
  if (Array.isArray(val) && val.length) {
    const joined = val.filter(Boolean).join(",");
    return joined || null;
  }
  if (val && String(val).trim()) return String(val).trim();
  return null;
};

/** Same API envelope helpers as AddEditUser (territory-master endpoints). */
export const parseApiEnvelope = (response) => {
  if (!response) return { ok: false, payload: null };
  const ok = response.success === true || response.Success === true;
  const payload = response.data ?? response.Data ?? null;
  return { ok, payload };
};

export const extractZonesFromResponse = (response) => {
  const { ok, payload } = parseApiEnvelope(response);
  if (!ok) return [];
  if (Array.isArray(payload)) return payload;
  return payload?.zones || payload?.Zones || [];
};

export const extractRegionsFromResponse = (response) => {
  const { ok, payload } = parseApiEnvelope(response);
  if (!ok) return [];
  if (Array.isArray(payload)) return payload;
  return payload?.regions || payload?.Regions || [];
};

export const normalizeZoneRow = (item, fallbackCountryCode) => ({
  zoneCode: item.zoneCode || item.ZoneCode || "",
  zoneName: item.zoneName || item.ZoneName || "",
  countryCode: item.countryCode || item.CountryCode || fallbackCountryCode || "",
  countryName: item.countryName || item.CountryName || "",
  isActive: item.isActive ?? item.IsActive ?? true,
});

export const normalizeRegionRow = (item) => ({
  regionCode: item.regionCode || item.RegionCode || "",
  regionName: item.regionName || item.RegionName || "",
  countryCode: item.countryCode || item.CountryCode || "",
  zoneCode: item.zoneCode || item.ZoneCode || "",
  isActive: item.isActive ?? item.IsActive ?? true,
});

export const normalizeTerritoryRow = (item) => ({
  territoryCode: item.territoryCode || item.TerritoryCode || "",
  territoryName: item.territoryName || item.TerritoryName || "",
  countryCode: item.countryCode || item.CountryCode || "",
  regionCode: item.regionCode || item.RegionCode || "",
  zoneCode: item.zoneCode || item.ZoneCode || "",
  isActive: item.isActive ?? item.IsActive ?? true,
});

export const mapZoneToOption = (z, countriesList, { includeCountry = true } = {}) => {
  const row = normalizeZoneRow(z, z.countryCode || z.CountryCode);
  return {
    id: row.zoneCode,
    zoneName: row.zoneName,
    countryCode: row.countryCode,
    countryName: row.countryName,
    countryCodes: row.countryCode ? [row.countryCode] : [],
    zoneCodes: row.zoneCode ? [row.zoneCode] : [],
    zoneCodeByCountry: row.countryCode && row.zoneCode
      ? { [row.countryCode]: row.zoneCode }
      : {},
    text: formatZoneOptionLabel(row, countriesList, { includeCountry }),
  };
};

/**
 * Export market: one dropdown row per zone name (e.g. AFRICA once).
 * Keeps all country + zone-code mappings so Country cascade and region loads work.
 */
export const mapExportZonesToOptions = (zoneRows, countriesList = []) => {
  const byName = new Map();
  (Array.isArray(zoneRows) ? zoneRows : []).forEach((z) => {
    const row = normalizeZoneRow(z, z.countryCode || z.CountryCode);
    if (!row.zoneCode) return;
    const nameKey = String(row.zoneName || row.zoneCode).trim().toUpperCase();
    if (!nameKey) return;
    if (!byName.has(nameKey)) {
      byName.set(nameKey, {
        id: row.zoneCode,
        zoneName: row.zoneName || row.zoneCode,
        countryCode: row.countryCode || "",
        countryName: row.countryName || "",
        countryCodes: [],
        zoneCodes: [],
        zoneCodeByCountry: {},
        text: formatZoneOptionLabel(row, countriesList, { includeCountry: false }),
      });
    }
    const opt = byName.get(nameKey);
    if (row.zoneCode && !opt.zoneCodes.includes(row.zoneCode)) {
      opt.zoneCodes.push(row.zoneCode);
    }
    if (row.countryCode) {
      if (!opt.countryCodes.includes(row.countryCode)) {
        opt.countryCodes.push(row.countryCode);
      }
      if (row.zoneCode) {
        opt.zoneCodeByCountry[row.countryCode] = row.zoneCode;
      }
      if (!opt.countryCode) {
        opt.countryCode = row.countryCode;
        opt.countryName = row.countryName || "";
      }
    }
  });
  return [...byName.values()].sort((a, b) =>
    String(a.text || "").localeCompare(String(b.text || ""), undefined, {
      sensitivity: "base",
    })
  );
};

/** Expand selected export zone option id(s) to all underlying ZoneMaster codes. */
export const expandZoneFilterCodes = (selectedIds, zoneOptions = []) => {
  const codes = [];
  const seen = new Set();
  const add = (code) => {
    const c = String(code ?? "").trim();
    if (!c || seen.has(c)) return;
    seen.add(c);
    codes.push(c);
  };
  (Array.isArray(selectedIds) ? selectedIds : []).forEach((id) => {
    const sid = String(id ?? "").trim();
    if (!sid) return;
    const opt = findZoneOption(zoneOptions, sid);
    if (opt?.zoneCodes?.length) {
      opt.zoneCodes.forEach(add);
    } else {
      add(sid);
    }
  });
  return codes;
};

/** Find a (possibly grouped) zone option by canonical id or any linked zoneCodes. */
export const findZoneOption = (zoneOptions = [], zoneOptionId) => {
  const zid = String(zoneOptionId ?? "").trim();
  if (!zid) return null;
  return (
    (zoneOptions || []).find((o) => {
      const id = String(o?.id ?? o?.zoneCode ?? o?.ZoneCode ?? "").trim();
      if (id === zid) return true;
      return (
        Array.isArray(o?.zoneCodes) &&
        o.zoneCodes.some((c) => String(c ?? "").trim() === zid)
      );
    }) || null
  );
};

/** Country codes linked to selected export zone option(s). */
export const getCountryCodesForSelectedZones = (selectedIds, zoneOptions = []) => {
  const codes = new Set();
  (Array.isArray(selectedIds) ? selectedIds : []).forEach((id) => {
    const opt = findZoneOption(zoneOptions, id);
    if (!opt) return;
    if (Array.isArray(opt.countryCodes) && opt.countryCodes.length) {
      opt.countryCodes.forEach((cc) => {
        const c = String(cc ?? "").trim();
        if (c) codes.add(c);
      });
    } else if (opt.countryCode) {
      codes.add(String(opt.countryCode).trim());
    }
  });
  return codes;
};

/** Resolve the ZoneMaster code for a country under a (possibly grouped) zone option. */
export const resolveZoneCodeForCountry = (
  zoneOptionId,
  countryCode,
  zoneOptions = []
) => {
  const zid = String(zoneOptionId ?? "").trim();
  const cc = String(countryCode ?? "").trim();
  if (!zid) return "";
  const opt = findZoneOption(zoneOptions, zid);
  if (!opt) return zid;
  if (cc && opt.zoneCodeByCountry?.[cc]) {
    return String(opt.zoneCodeByCountry[cc]).trim();
  }
  // Prefer a linked code that belongs to this country group; else canonical id.
  return String(opt.id || opt.zoneCode || zid).trim();
};

export const mapRegionToOption = (r) => ({
  id: r.regionCode,
  text: r.regionName || r.regionCode,
  zoneCode: r.zoneCode,
  countryCode: r.countryCode,
});

export const mapTerritoryToOption = (t) => ({
  id: t.territoryCode,
  text: t.territoryName || t.territoryCode,
  regionCode: t.regionCode,
});

/**
 * When user has ALL scope or exactly one option at a level, return ids to auto-select.
 * Export (M002): never auto-select zone — user picks zone(s) first.
 */
export const resolveGeoAutoSelectionIds = ({
  scopedOptions,
  user,
  level,
  isExportCascade = false,
  parentReady = true,
}) => {
  const scoped = Array.isArray(scopedOptions) ? scopedOptions : [];
  if (!user || !parentReady || !scoped.length) return null;
  if (level === "zone" && isExportCascade) return null;

  if (isUserGeoAllScopeForDashboard(user, level)) {
    const ids = resolveGeoFilterSelection(["all"], scoped);
    return ids.length ? ids : null;
  }

  if (scoped.length === 1) {
    const id = String(scoped[0]?.id ?? "").trim();
    return id ? [id] : null;
  }

  return null;
};

/** Country+zone pairs to query territories for a region (uses region row parents when available). */
export const resolveRegionGeoCandidates = (
  regionCode,
  regionRow,
  regionsList,
  zonesList,
  filterZoneCodes,
  filterCountryCodes
) => {
  const rc = String(regionCode ?? "").trim();
  if (!rc) return [];

  const row =
    regionRow ||
    (Array.isArray(regionsList) ? regionsList.find((x) => x.id === rc) : null);
  const rowCc = String(row?.countryCode ?? "").trim();
  const rowZc = String(row?.zoneCode ?? "").trim();
  if (rowCc && rowZc) return [{ cc: rowCc, zc: rowZc }];

  const pairs = [];
  const seen = new Set();
  const add = (cc, zc) => {
    const c = String(cc ?? "").trim();
    const z = String(zc ?? "").trim();
    if (!c || !z) return;
    const key = `${c}|${z}`;
    if (seen.has(key)) return;
    seen.add(key);
    pairs.push({ cc: c, zc: z });
  };

  const zones = (
    Array.isArray(filterZoneCodes) && filterZoneCodes.length
      ? filterZoneCodes
      : (Array.isArray(zonesList) ? zonesList.map((z) => z.id) : [])
  ).filter(Boolean);

  const countries = (
    Array.isArray(filterCountryCodes) ? filterCountryCodes : []
  ).filter(Boolean);

  for (const zId of zones) {
    const zRow = (zonesList || []).find((z) => z.id === zId);
    const byCountry = zRow?.zoneCodeByCountry;
    if (byCountry && typeof byCountry === "object" && Object.keys(byCountry).length) {
      const targetCountries = countries.length
        ? countries.filter((cc) => byCountry[cc] || (zRow.countryCodes || []).includes(cc))
        : Object.keys(byCountry);
      targetCountries.forEach((cc) => {
        add(cc, byCountry[cc] || zId);
      });
      continue;
    }
    if (zRow?.countryCode) {
      add(zRow.countryCode, zId);
    } else if (countries.length) {
      countries.forEach((cc) => add(cc, zId));
    }
  }

  return pairs;
};
